// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import validatePasswordRequirements from "@guestsupply/App/Hooks/validatePasswordRequirements";
import Success from "@guestsupply/XDL/Icons/Check";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

type Props = {
    newPassword: string;
    minLength: number;
    checkDigits: boolean;
    checkLowerCase: boolean;
    checkUpperCase: boolean;
    checkNonAlphaNumeric: boolean;
    // Xng-Changes
    passwordsMatching: boolean;
};

export interface PasswordRequirementsValidator {
    passwordRequirement?: InjectableCss;
    wrapper?: InjectableCss;
}

export const passwordRequirementsValidatorStyles: PasswordRequirementsValidator = {};

const PasswordRequirementValidator = ({
    newPassword,
    minLength,
    checkDigits,
    checkLowerCase,
    checkUpperCase,
    checkNonAlphaNumeric,
    // Xng-Changes
    passwordsMatching,
}: Props) => {
    const { hasLowerCase, hasUpperCase, hasNonAlphanumaric, hasDigit, hasMoreThanEightCharachters } =
        validatePasswordRequirements(
            newPassword,
            minLength,
            checkDigits,
            checkLowerCase,
            checkUpperCase,
            checkNonAlphaNumeric,
        );

    const styles = passwordRequirementsValidatorStyles;
    const renderIcon = (noChanges: boolean, condition: boolean) => {
        if (noChanges) {
            return null;
        }
        return condition ? (
            <Icon src={Success} size={11} color="success" />
        ) : (
            <Icon src={XCircle} size={11} color="danger" />
        );
    };

    const noChangesApplied = newPassword.length === 0;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                {...styles.passwordRequirement}
                {...{ passedRequirement: hasMoreThanEightCharachters, hasTyped: newPassword.length > 0 }}
            >
                <Typography className="icon">{renderIcon(noChangesApplied, hasMoreThanEightCharachters)}</Typography>
                <Typography className="text">
                    {translate("Password must be at least {0} characters long", minLength.toString())}
                </Typography>
            </StyledWrapper>
            {checkDigits && (
                <StyledWrapper
                    {...styles.passwordRequirement}
                    {...{ passedRequirement: hasDigit, hasTyped: newPassword.length > 0 }}
                >
                    <Typography className="icon">{renderIcon(noChangesApplied, hasDigit)}</Typography>
                    <Typography className="text">{translate("Password must include at least one number")}</Typography>
                </StyledWrapper>
            )}
            {checkLowerCase && (
                <StyledWrapper
                    {...styles.passwordRequirement}
                    {...{ passedRequirement: hasLowerCase, hasTyped: newPassword.length > 0 }}
                >
                    <Typography className="icon">{renderIcon(noChangesApplied, hasLowerCase)}</Typography>
                    <Typography className="text">
                        {translate("Password must include at least one lowercase character")}
                    </Typography>
                </StyledWrapper>
            )}
            {checkUpperCase && (
                <StyledWrapper
                    {...styles.passwordRequirement}
                    {...{ passedRequirement: hasUpperCase, hasTyped: newPassword.length > 0 }}
                >
                    <Typography className="icon">{renderIcon(noChangesApplied, hasUpperCase)}</Typography>
                    <Typography className="text">
                        {translate("Password must include at least one uppercase character")}
                    </Typography>
                </StyledWrapper>
            )}
            {checkNonAlphaNumeric && (
                <StyledWrapper
                    {...styles.passwordRequirement}
                    {...{ passedRequirement: hasNonAlphanumaric, hasTyped: newPassword.length > 0 }}
                >
                    <Typography className="icon">{renderIcon(noChangesApplied, hasNonAlphanumaric)}</Typography>
                    <Typography className="text">
                        {translate("Password must include at least one non alphanumeric character")}
                    </Typography>
                </StyledWrapper>
            )}
            <StyledWrapper
                {...styles.passwordRequirement}
                {...{ passedRequirement: passwordsMatching, hasTyped: newPassword.length > 0 }}
            >
                <Typography className="icon">{renderIcon(noChangesApplied, passwordsMatching)}</Typography>
                <Typography className="text">{translate("Password combination does not match")}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default PasswordRequirementValidator;
